import { CASE_FILE_TYPE_LABELS, type CaseFile, type CaseFileType } from '../../types/case'
import { groupFilesByType } from '../../lib/case-phase'
import { Button } from '../ui/button'

type CaseFilesGalleryProps = {
  files: CaseFile[]
  actionLoading: boolean
  canDownload: boolean
  canDelete: boolean
  onDownload: (fileId: string) => void
  onDelete: (fileId: string) => void
}

function FileCard({
  file,
  actionLoading,
  canDownload,
  canDelete,
  onDownload,
  onDelete,
}: {
  file: CaseFile
  actionLoading: boolean
  canDownload: boolean
  canDelete: boolean
  onDownload: () => void
  onDelete: () => void
}) {
  const isImage = (file.mime ?? '').startsWith('image/')

  return (
    <div className="overflow-hidden rounded-md border border-parch-100 bg-parch-50">
      {isImage && file.preview_url ? (
        <a
          href={file.preview_url}
          target="_blank"
          rel="noreferrer"
          className="block aspect-video bg-white"
        >
          <img
            src={file.preview_url}
            alt={file.original_name}
            className="h-full w-full object-contain"
          />
        </a>
      ) : (
        <div className="flex aspect-video items-center justify-center bg-white text-sm text-ink-500">
          {file.mime?.includes('pdf') ? 'PDF' : 'File'}
        </div>
      )}
      <div className="space-y-2 px-3 py-3">
        <div>
          <p className="truncate text-sm font-medium text-ink-800">{file.original_name}</p>
          <p className="text-xs text-ink-500">{Math.round(file.size / 1024)} KB</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canDownload && (
            <Button size="sm" variant="secondary" onClick={onDownload}>
              Download
            </Button>
          )}
          {canDelete && (
            <Button size="sm" variant="destructive" disabled={actionLoading} onClick={onDelete}>
              Delete
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

function FileGroup({
  type,
  files,
  actionLoading,
  canDownload,
  canDelete,
  onDownload,
  onDelete,
}: {
  type: CaseFileType
  files: CaseFile[]
  actionLoading: boolean
  canDownload: boolean
  canDelete: boolean
  onDownload: (fileId: string) => void
  onDelete: (fileId: string) => void
}) {
  if (files.length === 0) {
    return null
  }

  return (
    <section className="space-y-3">
      <h3 className="font-display text-base font-semibold text-ink-900">
        {CASE_FILE_TYPE_LABELS[type]} images / files
      </h3>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {files.map((file) => (
          <FileCard
            key={file.id}
            file={file}
            actionLoading={actionLoading}
            canDownload={canDownload}
            canDelete={canDelete}
            onDownload={() => onDownload(file.id)}
            onDelete={() => onDelete(file.id)}
          />
        ))}
      </div>
    </section>
  )
}

/** Legacy-style gallery grouped by pre-op and post-op. */
export function CaseFilesGallery({
  files,
  actionLoading,
  canDownload,
  canDelete,
  onDownload,
  onDelete,
}: CaseFilesGalleryProps) {
  const grouped = groupFilesByType(files)

  if (files.length === 0) {
    return <p className="text-sm text-ink-500">No files uploaded.</p>
  }

  return (
    <div className="space-y-8">
      <FileGroup
        type="pre_op"
        files={grouped.pre_op}
        actionLoading={actionLoading}
        canDownload={canDownload}
        canDelete={canDelete}
        onDownload={onDownload}
        onDelete={onDelete}
      />
      <FileGroup
        type="post_op"
        files={grouped.post_op}
        actionLoading={actionLoading}
        canDownload={canDownload}
        canDelete={canDelete}
        onDownload={onDownload}
        onDelete={onDelete}
      />
      <FileGroup
        type="outcome"
        files={grouped.outcome}
        actionLoading={actionLoading}
        canDownload={canDownload}
        canDelete={canDelete}
        onDownload={onDownload}
        onDelete={onDelete}
      />
      {grouped.pre_op.length === 0 &&
        grouped.post_op.length === 0 &&
        grouped.outcome.length === 0 && (
        <p className="text-sm text-ink-500">No typed files found.</p>
      )}
    </div>
  )
}
